import { SubmissionGuideOffer, recordSubmissionGuideOffer } from './SubmissionGuideOffer';
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Captions,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Download,
  Film,
  Info,
  Link2,
  ListVideo,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
  Scissors,
  SlidersHorizontal,
  Timer,
  Type,
  Video,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  signInPathForDestination,
  storeAuthRedirectDestination,
} from "../../lib/authRedirect";
import {
  claimLandingSourceIntent,
  clearLandingSourceIntentForOwner,
  extractLandingYoutubeVideoId,
  storeLandingSourceIntent,
  validateLandingYoutubeUrl,
} from "../../lib/landingSourceIntent";
import {
  TOPCLIP_AUTO_CAPTION_STYLES,
  TOPCLIP_AUTO_LENGTH_CHOICES,
  TopClipAutoApiError,
  TopClipAutoClip,
  TopClipAutoJobStatus,
  TopClipAutoOptions,
  createDefaultTopClipAutoOptions,
  downloadTopClipAutoClip,
  getTopClipAutoClips,
  getTopClipAutoJobStatus,
  submitTopClipAutoJob,
} from "./topclipAutoApi";
import {
  getManualClipCountMaximum,
  TOPCLIP_CLIP_COUNT_PRODUCT_MAX,
} from "./topclipAutoClipCount";
import {
  clearTopClipAutoJobTiming,
  estimatedTopClipAutoProgressPercent,
  estimatedTopClipAutoStageIndex,
  readTopClipAutoJobTiming,
  saveTopClipAutoJobTiming,
  TOPCLIP_AUTO_PROGRESS_STAGES,
  TopClipAutoJobTiming,
} from "./topclipAutoProgress";
import { useYoutubeDuration } from "./useYoutubeDuration";
import "./TopClipAutoShell.css";

const STATUS_POLL_INTERVAL_MS = 5_000;
const NETWORK_RETRY_INTERVAL_MS = 8_000;
const SOURCE_PREVIEW_DEBOUNCE_MS = 350;
const SOURCE_PREVIEW_TIMEOUT_MS = 8_000;
const MIN_SOURCE_DURATION_SECONDS = 300;
const MAX_SOURCE_DURATION_SECONDS = 5_400;
const ACTIVE_STATUSES = new Set(["queued", "processing"]);
const TERMINAL_FAILURE_STATUSES = new Set(["error", "failed"]);
const MANUAL_COUNT_TOOLTIP =
  "This is a target, not a guarantee. The final number depends on video length and how many distinct, high-quality moments TopClip finds.";
const IMPOSSIBLE_CLIP_COUNT_MESSAGE =
  "That video is too short for this number of clips at the selected length. Choose fewer clips or a shorter length.";
const SOURCE_DURATION_ERROR_MESSAGE =
  "This video must be between 5 and 90 minutes.";

const FEATURED_CAPTION_STYLE_IDS: readonly TopClipAutoOptions["caption_style_id"][] =
  ["basic", "word-focus", "green-glow", "lowkey"];

type SourcePreviewState =
  | "empty"
  | "loading"
  | "ready"
  | "invalid"
  | "unavailable";

type JobProblem = {
  kind: "network" | "terminal" | "request" | "authentication";
  message: string;
};

type RetryWait = {
  message: string;
  seconds: number | null;
};

type LoadedJobTiming = {
  jobId: string;
  timing: TopClipAutoJobTiming;
};

type TrackerStageState = {
  jobId: string;
  index: number;
};

const readableError = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.trim() !== ""
    ? error.message
    : fallback;

const knownSourceDurationError = (
  durationSeconds: number | null
): string | null =>
  durationSeconds !== null &&
  Number.isFinite(durationSeconds) &&
  (durationSeconds < MIN_SOURCE_DURATION_SECONDS ||
    durationSeconds > MAX_SOURCE_DURATION_SECONDS)
    ? SOURCE_DURATION_ERROR_MESSAGE
    : null;

const formatDuration = (seconds: number | undefined): string | null => {
  if (seconds === undefined || seconds <= 0) return null;
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds === 0
    ? `${minutes} min`
    : `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const thumbnailUrlForVideoId = (videoId: string): string =>
  `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;

const processingStageIndex = (
  status: TopClipAutoJobStatus | null
): number => {
  if (!status) return 0;
  if (status.status === "queued") return 1;
  const step = `${status.current_step ?? ""} ${status.message ?? ""}`.toLowerCase();
  if (
    step.includes("final") ||
    step.includes("publish") ||
    step.includes("upload") ||
    step.includes("sav") ||
    status.progress >= 90
  ) {
    return 4;
  }
  if (
    step.includes("caption") ||
    step.includes("fram") ||
    step.includes("cut") ||
    step.includes("render") ||
    step.includes("creat") ||
    status.progress >= 60
  ) {
    return 3;
  }
  if (
    step.includes("prepar") ||
    step.includes("source") ||
    step.includes("import") ||
    step.includes("download") ||
    status.progress <= 15
  ) {
    return 1;
  }
  if (
    step.includes("find") ||
    step.includes("moment") ||
    step.includes("scor") ||
    step.includes("transcrib") ||
    status.progress > 15
  ) {
    return 2;
  }
  return 2;
};

const STAGE_PROGRESS_FLOORS = [8, 20, 40, 60, 80] as const;

const ProgressTracker = ({
  activeStage,
  progressPercent,
}: {
  activeStage: number;
  progressPercent: number;
}) => (
  <>
    <div className="tc-create-stage-list">
      {TOPCLIP_AUTO_PROGRESS_STAGES.map((label, index) => {
        const complete = index < activeStage;
        const active = index === activeStage;
        return (
          <div
            key={label}
            className={`tc-create-stage ${
              complete ? "is-complete" : active ? "is-active" : ""
            }`}
          >
            <span className="tc-create-stage__marker">
              {complete ? (
                <Check aria-hidden="true" />
              ) : active ? (
                <Loader2 className="tc-create-spin" aria-hidden="true" />
              ) : (
                index + 1
              )}
            </span>
            <div>
              <strong>{label}</strong>
            </div>
          </div>
        );
      })}
    </div>
    <div
      className="tc-create-server-progress"
      role="progressbar"
      aria-label="Progress tracker"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progressPercent)}
    >
      <span>
        <span style={{ width: `${progressPercent}%` }} />
      </span>
      <small>Progress tracker</small>
    </div>
  </>
);

const Journey = ({
  current,
}: {
  current: "video" | "settings" | "clips";
}) => {
  const steps = [
    { id: "video", label: "Video", icon: Video },
    { id: "settings", label: "Settings", icon: SlidersHorizontal },
    { id: "clips", label: "Clips", icon: Film },
  ] as const;
  const currentIndex = steps.findIndex((step) => step.id === current);

  return (
    <ol className="tc-create-journey" aria-label="Create clips progress">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const complete = index < currentIndex;
        const active = index === currentIndex;
        return (
          <React.Fragment key={step.id}>
            <li
              className={`tc-create-journey__step ${
                active ? "is-active" : complete ? "is-complete" : ""
              }`}
              aria-current={active ? "step" : undefined}
            >
              <span className="tc-create-journey__icon">
                {complete ? (
                  <Check aria-hidden="true" />
                ) : (
                  <Icon aria-hidden="true" />
                )}
              </span>
              <span>{step.label}</span>
            </li>
            {index < steps.length - 1 && (
              <li className="tc-create-journey__connector" aria-hidden="true">
                <ChevronRight />
              </li>
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
};

const Toggle = ({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`tc-create-toggle ${checked ? "is-on" : ""}`}
    onClick={() => onChange(!checked)}
  >
    <span className="tc-create-toggle__thumb" />
  </button>
);

const CaptionSample = ({
  styleId,
  compact = false,
}: {
  styleId: TopClipAutoOptions["caption_style_id"];
  compact?: boolean;
}) => (
  <span
    className={`tc-create-caption-sample ${compact ? "is-compact" : ""}`}
    data-caption-style={styleId}
    aria-hidden="true"
  >
    <span>YOUR NEXT MOVE</span>
  </span>
);

const CaptionStyleDialog = ({
  selected,
  onSelect,
  onClose,
}: {
  selected: TopClipAutoOptions["caption_style_id"];
  onSelect: (style: TopClipAutoOptions["caption_style_id"]) => void;
  onClose: () => void;
}) => {
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="tc-create-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="tc-create-caption-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tc-create-caption-dialog-title"
      >
        <header className="tc-create-caption-dialog__header">
          <div>
            <h2 id="tc-create-caption-dialog-title">
              Find your caption style
            </h2>
            <p>All 18 supported looks are available here.</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="tc-create-icon-button"
            aria-label="Close caption styles"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="tc-create-caption-dialog__grid">
          {TOPCLIP_AUTO_CAPTION_STYLES.map((style) => (
            <button
              type="button"
              key={style.id}
              aria-pressed={selected === style.id}
              className={`tc-create-caption-choice ${
                selected === style.id ? "is-selected" : ""
              }`}
              onClick={() => {
                onSelect(style.id);
                onClose();
              }}
            >
              <CaptionSample styleId={style.id} compact />
              <span>
                {style.label}
                {selected === style.id && (
                  <CheckCircle2 aria-hidden="true" />
                )}
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

const SourcePreview = ({
  state,
  videoId,
  message,
  previewKey,
  onLoaded,
  onUnavailable,
  onReplace,
  onRetry,
}: {
  state: SourcePreviewState;
  videoId: string | null;
  message: string | null;
  previewKey: number;
  onLoaded: () => void;
  onUnavailable: () => void;
  onReplace: () => void;
  onRetry: () => void;
}) => {
  if (state === "empty") {
    return (
      <div className="tc-create-source-state is-empty">
        <span className="tc-create-source-state__icon">
          <Play aria-hidden="true" />
        </span>
        <div>
          <strong>Your video will appear here</strong>
          <p>Paste a YouTube link to add a source preview.</p>
        </div>
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="tc-create-source-state is-error" role="alert">
        <span className="tc-create-source-state__icon">
          <AlertCircle aria-hidden="true" />
        </span>
        <div>
          <strong>That link is not ready yet</strong>
          <p>{message || "Paste a supported YouTube video link."}</p>
        </div>
      </div>
    );
  }

  if (state === "unavailable" && videoId) {
    return (
      <div className="tc-create-source-state is-unavailable" role="status">
        <span className="tc-create-source-state__icon">
          <Video aria-hidden="true" />
        </span>
        <div>
          <strong>The link is valid, but its thumbnail is unavailable</strong>
          <p>
            You can still try this video. TopClip will verify the source during
            submission.
          </p>
          <div className="tc-create-source-state__actions">
            <button type="button" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              Retry preview
            </button>
            <button type="button" onClick={onReplace}>
              Replace video
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!videoId) {
    return (
      <div className="tc-create-source-state is-loading" role="status">
        <span className="tc-create-source-state__icon">
          <Loader2 className="tc-create-spin" aria-hidden="true" />
        </span>
        <div>
          <strong>Checking your YouTube link</strong>
          <p>Validating the link format.</p>
        </div>
      </div>
    );
  }

  return (
    <article
      className={`tc-create-source-card ${
        state === "ready" ? "is-ready" : "is-loading"
      }`}
      aria-live="polite"
    >
      <div className="tc-create-source-card__media">
        <img
          key={`${videoId}-${previewKey}`}
          src={thumbnailUrlForVideoId(videoId)}
          alt="YouTube source thumbnail"
          onLoad={onLoaded}
          onError={onUnavailable}
          className={state === "ready" ? "is-visible" : ""}
        />
        {state === "loading" && (
          <div className="tc-create-source-card__skeleton" aria-hidden="true">
            <Loader2 className="tc-create-spin" />
          </div>
        )}
        <span className="tc-create-source-card__origin">
          <Play aria-hidden="true" />
          YouTube
        </span>
      </div>
      <div className="tc-create-source-card__copy">
        <span className="tc-create-source-card__status">
          {state === "ready" ? (
            <CheckCircle2 aria-hidden="true" />
          ) : (
            <Loader2 className="tc-create-spin" aria-hidden="true" />
          )}
          {state === "ready" ? "Preview ready" : "Loading thumbnail"}
        </span>
        <h3>
          {state === "ready"
            ? "YouTube video added"
            : "Checking this video"}
        </h3>
        <p>
          {state === "ready"
            ? "TopClip will verify the source during submission."
            : "The link is valid. The preview is still loading."}
        </p>
      </div>
      <button
        type="button"
        className="tc-create-icon-button tc-create-source-card__replace"
        aria-label="Replace video"
        onClick={onReplace}
      >
        <RotateCcw aria-hidden="true" />
      </button>
    </article>
  );
};

const CountControl = ({
  options,
  updateOptions,
  manualMaximum,
}: {
  options: TopClipAutoOptions;
  updateOptions: (update: Partial<TopClipAutoOptions>) => void;
  manualMaximum: number | null;
}) => (
  <div className="tc-create-control-group">
    <div
      className="tc-create-segmented"
      role="radiogroup"
      aria-label="Clip count mode"
    >
      {(["auto", "manual"] as const).map((mode) => (
        <button
          type="button"
          role="radio"
          aria-checked={options.clip_count_mode === mode}
          aria-label={
            mode === "manual"
              ? `Manual ${options.clips_requested} ${
                  options.clips_requested === 1 ? "clip" : "clips"
                }`
              : undefined
          }
          aria-describedby={
            mode === "manual" ? "tc-create-manual-count-tooltip" : undefined
          }
          key={mode}
          className={options.clip_count_mode === mode ? "is-selected" : ""}
          onClick={() =>
            updateOptions({
              clip_count_mode: mode,
              clips_requested:
                mode === "auto"
                  ? 6
                  : Math.min(
                      options.clips_requested,
                      manualMaximum ?? TOPCLIP_CLIP_COUNT_PRODUCT_MAX
                    ),
            })
          }
        >
          <span>
            {mode === "auto" ? (
              "Auto"
            ) : (
              <>
                Manual
                <Info className="tc-create-manual-info" aria-hidden="true" />
              </>
            )}
          </span>
          <small>
            {mode === "auto"
              ? "Recommended"
              : `${options.clips_requested} ${
                  options.clips_requested === 1 ? "clip" : "clips"
                }`}
          </small>
          {mode === "manual" && (
            <span
              id="tc-create-manual-count-tooltip"
              className="tc-create-manual-tooltip"
              role="tooltip"
            >
              {MANUAL_COUNT_TOOLTIP}
            </span>
          )}
        </button>
      ))}
    </div>
    {options.clip_count_mode === "auto" ? (
      <p className="tc-create-control-note">
        TopClip chooses the number of distinct moments for this video.
      </p>
    ) : (
      <>
        <div
          className="tc-create-number-grid"
          role="group"
          aria-label="Number of clips"
        >
          {Array.from(
            { length: TOPCLIP_CLIP_COUNT_PRODUCT_MAX },
            (_, index) => index + 1
          ).map((count) => {
            const isUnavailable =
              manualMaximum !== null && count > manualMaximum;
            return (
              <button
                type="button"
                key={count}
                aria-label={`${count} ${count === 1 ? "clip" : "clips"}`}
                aria-pressed={options.clips_requested === count}
                aria-disabled={isUnavailable}
                disabled={isUnavailable}
                className={
                  options.clips_requested === count ? "is-selected" : ""
                }
                onClick={() => updateOptions({ clips_requested: count })}
              >
                {count}
              </button>
            );
          })}
        </div>
      </>
    )}
  </div>
);

const LengthControl = ({
  options,
  updateOptions,
}: {
  options: TopClipAutoOptions;
  updateOptions: (update: Partial<TopClipAutoOptions>) => void;
}) => (
  <div
    className="tc-create-length-grid"
    role="radiogroup"
    aria-label="Clip length"
  >
    {TOPCLIP_AUTO_LENGTH_CHOICES.map((choice) => {
      const supportingCopy = {
        auto: "Best fit",
        short: "15-30 sec",
        medium: "30-60 sec",
        long: "60-90 sec",
      }[choice.id];
      return (
        <button
          type="button"
          role="radio"
          aria-checked={options.clip_length_choice === choice.id}
          key={choice.id}
          className={
            options.clip_length_choice === choice.id ? "is-selected" : ""
          }
          onClick={() => updateOptions({ clip_length_choice: choice.id })}
        >
          <Timer aria-hidden="true" />
          <span>{choice.id === "auto" ? "Auto" : choice.label.split(" ")[0]}</span>
          <small>{supportingCopy}</small>
        </button>
      );
    })}
  </div>
);

const CaptionControls = ({
  options,
  updateOptions,
  onBrowse,
}: {
  options: TopClipAutoOptions;
  updateOptions: (update: Partial<TopClipAutoOptions>) => void;
  onBrowse: () => void;
}) => (
  <div className="tc-create-captions">
    {options.captions_enabled ? (
      <>
        <div className="tc-create-caption-featured">
          {FEATURED_CAPTION_STYLE_IDS.map((styleId) => {
            const style = TOPCLIP_AUTO_CAPTION_STYLES.find(
              (candidate) => candidate.id === styleId
            );
            return (
              <button
                type="button"
                key={styleId}
                aria-pressed={options.caption_style_id === styleId}
                className={
                  options.caption_style_id === styleId ? "is-selected" : ""
                }
                onClick={() => updateOptions({ caption_style_id: styleId })}
              >
                <CaptionSample styleId={styleId} />
                <span>{style?.label}</span>
                {options.caption_style_id === styleId && (
                  <CheckCircle2
                    className="tc-create-choice-check"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="tc-create-browse-button"
          onClick={onBrowse}
        >
          <ListVideo aria-hidden="true" />
          Browse all 18 styles
          <ArrowRight aria-hidden="true" />
        </button>
      </>
    ) : (
      <div className="tc-create-captions-off" role="status">
        <Captions aria-hidden="true" />
        <span>Captions are off. Turn them on to choose a visual style.</span>
      </div>
    )}
  </div>
);

const SelectionSummary = ({ options }: { options: TopClipAutoOptions }) => {
  const selectedStyle = TOPCLIP_AUTO_CAPTION_STYLES.find(
    (style) => style.id === options.caption_style_id
  );
  const selectedLength = TOPCLIP_AUTO_LENGTH_CHOICES.find(
    (choice) => choice.id === options.clip_length_choice
  );
  return (
    <ul className="tc-create-selection-summary" aria-label="Selected settings">
      <li>
        <Scissors aria-hidden="true" />
        {options.clip_count_mode === "auto"
          ? "Auto count"
          : `${options.clips_requested} clips`}
      </li>
      <li>
        <Timer aria-hidden="true" />
        {selectedLength?.label || "Auto length"}
      </li>
      <li>
        <Captions aria-hidden="true" />
        {options.captions_enabled
          ? `${selectedStyle?.label || "Basic"} captions`
          : "Captions off"}
      </li>
      <li>
        <Type aria-hidden="true" />
        {options.ai_title_enabled ? "Titles on" : "Titles off"}
      </li>
    </ul>
  );
};

export const TopClipAutoShell: React.FC = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const accessToken = session?.access_token ?? "";
  const submissionSessionRef = useRef({ ownerId: user?.id, accessToken });
  submissionSessionRef.current = { ownerId: user?.id, accessToken };
  useEffect(() => {
    submissionSessionRef.current = { ownerId: user?.id, accessToken };
    return () => {
      submissionSessionRef.current = { ownerId: undefined, accessToken: "" };
    };
  }, [user?.id, accessToken]);
  const displayedJobIdRef = useRef(jobId);
  const urlInputRef = useRef<HTMLInputElement>(null);
  displayedJobIdRef.current = jobId;

  const [youtubeUrl, setYoutubeUrl] = useState("");
  const sourceIntentOwnerRef = useRef<string | null>(null);
  const prefilledSourceUrlRef = useRef<string | null>(null);
  const [sourcePreviewState, setSourcePreviewState] =
    useState<SourcePreviewState>("empty");
  const [sourcePreviewMessage, setSourcePreviewMessage] = useState<string | null>(
    null
  );
  const [sourceVideoId, setSourceVideoId] = useState<string | null>(null);
  const [sourcePreviewKey, setSourcePreviewKey] = useState(0);
  const [options, setOptions] = useState<TopClipAutoOptions>(
    createDefaultTopClipAutoOptions
  );
  const [captionDialogOpen, setCaptionDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitAccessIssue, setSubmitAccessIssue] = useState(false);
  const [retryWait, setRetryWait] = useState<RetryWait | null>(null);
  const [submissionUnknown, setSubmissionUnknown] = useState(false);

  const [jobStatus, setJobStatus] = useState<TopClipAutoJobStatus | null>(null);
  const [jobProblem, setJobProblem] = useState<JobProblem | null>(null);
  const [pollVersion, setPollVersion] = useState(0);
  const [loadedJobTiming, setLoadedJobTiming] =
    useState<LoadedJobTiming | null>(null);
  const [estimateClockMs, setEstimateClockMs] = useState(Date.now);
  const [trackerStageState, setTrackerStageState] =
    useState<TrackerStageState | null>(null);
  const [clips, setClips] = useState<TopClipAutoClip[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [downloadingFilename, setDownloadingFilename] = useState<string | null>(
    null
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const sourceValidation = useMemo(
    () => validateLandingYoutubeUrl(youtubeUrl),
    [youtubeUrl]
  );
  const validatedSourceVideoId = useMemo(
    () => extractLandingYoutubeVideoId(youtubeUrl),
    [youtubeUrl]
  );
  const sourceDuration = useYoutubeDuration(
    validatedSourceVideoId,
    !jobId && Boolean(validatedSourceVideoId)
  );
  const sourceDurationError =
    sourceDuration.status === "ready"
      ? knownSourceDurationError(sourceDuration.durationSeconds)
      : null;
  const sourceDurationPending =
    sourceValidation.ok &&
    (sourceDuration.status === "idle" || sourceDuration.status === "loading");
  const sourceCanSubmit =
    sourceValidation.ok && !sourceDurationPending && !sourceDurationError;
  const manualClipCountMaximum = useMemo(
    () =>
      getManualClipCountMaximum({
        sourceDurationSeconds: sourceDuration.durationSeconds,
        lengthChoice: options.clip_length_choice,
      }),
    [options.clip_length_choice, sourceDuration.durationSeconds]
  );

  useEffect(() => {
    if (jobId) return;
    const ownerId = user?.id;
    const previousOwnerId = sourceIntentOwnerRef.current;

    if (!ownerId) {
      if (previousOwnerId !== null) setYoutubeUrl("");
      sourceIntentOwnerRef.current = null;
      prefilledSourceUrlRef.current = null;
      return;
    }

    const ownerChanged =
      previousOwnerId !== null && previousOwnerId !== ownerId;
    sourceIntentOwnerRef.current = ownerId;
    const intent = claimLandingSourceIntent(ownerId);
    if (intent) {
      prefilledSourceUrlRef.current = intent.url;
      setYoutubeUrl(intent.url);
    } else if (ownerChanged) {
      prefilledSourceUrlRef.current = null;
      setYoutubeUrl("");
    }
  }, [jobId, user?.id]);

  useEffect(() => {
    if (jobId) return;
    const candidate = youtubeUrl;
    setSourcePreviewMessage(null);

    if (candidate === "") {
      setSourceVideoId(null);
      setSourcePreviewState("empty");
      return;
    }

    setSourceVideoId(null);
    setSourcePreviewState("loading");
    const timer = window.setTimeout(() => {
      const validation = validateLandingYoutubeUrl(candidate);
      const videoId = extractLandingYoutubeVideoId(candidate);
      if (!validation.ok || !videoId) {
        setSourcePreviewMessage(
          validation.ok
            ? "Paste a supported YouTube video link."
            : validation.message
        );
        setSourcePreviewState("invalid");
        return;
      }
      setSourceVideoId(videoId);
      setSourcePreviewState("loading");
    }, SOURCE_PREVIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [jobId, youtubeUrl]);

  useEffect(() => {
    if (!sourceVideoId || sourcePreviewState !== "loading") return;
    const timer = window.setTimeout(
      () => setSourcePreviewState("unavailable"),
      SOURCE_PREVIEW_TIMEOUT_MS
    );
    return () => window.clearTimeout(timer);
  }, [sourcePreviewKey, sourcePreviewState, sourceVideoId]);

  useEffect(() => {
    if (retryWait?.seconds === null || retryWait === null) return;
    if (retryWait.seconds <= 0) return;
    const interval = window.setInterval(() => {
      setRetryWait((current) => {
        if (current === null || current.seconds === null) return current;
        return { ...current, seconds: Math.max(0, current.seconds - 1) };
      });
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [retryWait]);

  useEffect(() => {
    if (
      options.clip_count_mode !== "manual" ||
      manualClipCountMaximum === null ||
      options.clips_requested <= manualClipCountMaximum
    ) {
      return;
    }
    setOptions((current) =>
      current.clip_count_mode === "manual" &&
      current.clips_requested > manualClipCountMaximum
        ? { ...current, clips_requested: manualClipCountMaximum }
        : current
    );
  }, [
    manualClipCountMaximum,
    options.clip_count_mode,
    options.clips_requested,
  ]);

  useEffect(() => {
    const ownerId = user?.id;
    if (!jobId || !ownerId) {
      setLoadedJobTiming(null);
      setTrackerStageState(null);
      return;
    }

    const timing = readTopClipAutoJobTiming(ownerId, jobId);
    const nowMs = Date.now();
    setEstimateClockMs(nowMs);
    setLoadedJobTiming(timing ? { jobId, timing } : null);
    setTrackerStageState({
      jobId,
      index: timing ? estimatedTopClipAutoStageIndex(timing, nowMs) : 0,
    });
  }, [jobId, user?.id]);

  const jobTiming =
    loadedJobTiming && loadedJobTiming.jobId === jobId
      ? loadedJobTiming.timing
      : null;

  useEffect(() => {
    if (!jobTiming) return;

    const refreshFromAbsoluteTime = () => setEstimateClockMs(Date.now());
    const interval = window.setInterval(refreshFromAbsoluteTime, 1_000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshFromAbsoluteTime();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [jobTiming]);

  const currentServerStatus =
    jobStatus?.job_id === jobId ? jobStatus : null;
  const estimatedStageCandidate = jobTiming
    ? estimatedTopClipAutoStageIndex(jobTiming, estimateClockMs)
    : 0;
  const serverStageCandidate = currentServerStatus
    ? processingStageIndex(currentServerStatus)
    : 0;

  useEffect(() => {
    if (!jobId) return;
    const nextIndex = Math.max(
      estimatedStageCandidate,
      serverStageCandidate
    );
    setTrackerStageState((current) => ({
      jobId,
      index:
        current?.jobId === jobId
          ? Math.max(current.index, nextIndex)
          : nextIndex,
    }));
  }, [estimatedStageCandidate, jobId, serverStageCandidate]);

  const retryResults = useCallback(async () => {
    if (!jobId || !accessToken) {
      setResultsError("Your sign-in session is unavailable. Please sign in again.");
      return;
    }
    const requestedJobId = jobId;
    setResultsLoading(true);
    setResultsError(null);
    try {
      const result = await getTopClipAutoClips(requestedJobId, accessToken);
      if (displayedJobIdRef.current !== requestedJobId) return;
      setClips(result.clips);
    } catch (error) {
      if (displayedJobIdRef.current !== requestedJobId) return;
      setResultsError(
        error instanceof TopClipAutoApiError && error.kind === "network"
          ? "Your clips are ready, but they are temporarily unreachable. Check your connection and try again."
          : readableError(error, "Your clips could not be loaded.")
      );
    } finally {
      if (displayedJobIdRef.current === requestedJobId) {
        setResultsLoading(false);
      }
    }
  }, [accessToken, jobId]);

  useEffect(() => {
    if (!jobId) return;
    const requestedJobId = jobId;

    setJobStatus(null);
    setJobProblem(null);
    setClips([]);
    setResultsError(null);
    setResultsLoading(false);
    setDownloadError(null);

    if (!accessToken) {
      setJobProblem({
        kind: "authentication",
        message: "Your sign-in session is unavailable. Please sign in again.",
      });
      return;
    }

    let cancelled = false;
    let timer: number | undefined;

    const poll = async (): Promise<void> => {
      try {
        const status = await getTopClipAutoJobStatus(requestedJobId, accessToken);
        if (cancelled || displayedJobIdRef.current !== requestedJobId) return;
        setJobStatus(status);
        setJobProblem(null);

        if (status.status === "completed") {
          if (user?.id) {
            clearTopClipAutoJobTiming(user.id, requestedJobId);
          }
          setLoadedJobTiming((current) =>
            current?.jobId === requestedJobId ? null : current
          );
          setResultsLoading(true);
          setResultsError(null);
          try {
            const result = await getTopClipAutoClips(requestedJobId, accessToken);
            if (!cancelled && displayedJobIdRef.current === requestedJobId) {
              setClips(result.clips);
            }
          } catch (error) {
            if (!cancelled && displayedJobIdRef.current === requestedJobId) {
              setResultsError(
                error instanceof TopClipAutoApiError && error.kind === "network"
                  ? "Your clips are ready, but they are temporarily unreachable. Check your connection and try again."
                  : readableError(error, "Your clips could not be loaded.")
              );
            }
          } finally {
            if (!cancelled && displayedJobIdRef.current === requestedJobId) {
              setResultsLoading(false);
            }
          }
          return;
        }

        if (ACTIVE_STATUSES.has(status.status)) {
          timer = window.setTimeout(poll, STATUS_POLL_INTERVAL_MS);
          return;
        }

        if (user?.id) {
          clearTopClipAutoJobTiming(user.id, requestedJobId);
        }
        setLoadedJobTiming((current) =>
          current?.jobId === requestedJobId ? null : current
        );
        setJobProblem({
          kind: "terminal",
          message: TERMINAL_FAILURE_STATUSES.has(status.status)
            ? status.message || "TopClip could not finish this video."
            : "TopClip returned an unsupported generation state.",
        });
      } catch (error) {
        if (cancelled || displayedJobIdRef.current !== requestedJobId) return;
        if (error instanceof TopClipAutoApiError && error.kind === "network") {
          setJobProblem({
            kind: "network",
            message:
              "Your clips have not been marked complete or failed. TopClip will keep checking automatically.",
          });
          timer = window.setTimeout(poll, NETWORK_RETRY_INTERVAL_MS);
          return;
        }
        setJobProblem({
          kind:
            error instanceof TopClipAutoApiError &&
            error.kind === "authentication"
              ? "authentication"
              : "request",
          message: readableError(error, "This job could not be loaded."),
        });
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [accessToken, jobId, pollVersion, user?.id]);

  const updateOptions = (update: Partial<TopClipAutoOptions>) => {
    setOptions((current) => ({ ...current, ...update }));
  };

  const handleUrlChange = (nextUrl: string) => {
    if (
      prefilledSourceUrlRef.current !== null &&
      nextUrl !== prefilledSourceUrlRef.current
    ) {
      clearLandingSourceIntentForOwner(user?.id);
      prefilledSourceUrlRef.current = null;
    }
    setSubmitError(null);
    setSubmitAccessIssue(false);
    setYoutubeUrl(nextUrl);
  };

  const replaceSource = () => {
    clearLandingSourceIntentForOwner(user?.id);
    prefilledSourceUrlRef.current = null;
    setYoutubeUrl("");
    setSourceVideoId(null);
    setSourcePreviewState("empty");
    setSubmitError(null);
    setSubmitAccessIssue(false);
    window.setTimeout(() => urlInputRef.current?.focus(), 0);
  };

  const retrySourcePreview = () => {
    if (!sourceVideoId) return;
    setSourcePreviewState("loading");
    setSourcePreviewKey((value) => value + 1);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!accessToken) {
      setSubmitError("Your sign-in session is unavailable. Please sign in again.");
      return;
    }
    const validation = validateLandingYoutubeUrl(youtubeUrl);
    if (!validation.ok) {
      setSourcePreviewMessage(validation.message);
      setSourcePreviewState("invalid");
      urlInputRef.current?.focus();
      return;
    }
    if (
      sourceDuration.status === "idle" ||
      sourceDuration.status === "loading" ||
      (sourceDuration.status === "ready" &&
        knownSourceDurationError(sourceDuration.durationSeconds))
    ) {
      return;
    }
    if (submissionUnknown || (retryWait?.seconds ?? 0) > 0) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitAccessIssue(false);
    setRetryWait(null);
    try {
      const result = await submitTopClipAutoJob(
        validation.url,
        options,
        accessToken
      );
      if (user?.id) {
        saveTopClipAutoJobTiming(
          user.id,
          result.job_id,
          sourceDuration.status === "ready"
            ? sourceDuration.durationSeconds
            : null
        );
      }
      if (user?.id) recordSubmissionGuideOffer(user.id, result.job_id);
      clearLandingSourceIntentForOwner(user?.id);
      prefilledSourceUrlRef.current = null;
      navigate(`/create-clips/${encodeURIComponent(result.job_id)}`);
    } catch (error) {
      if (
        error instanceof TopClipAutoApiError &&
        error.kind === "request" &&
        error.status === 403 &&
        error.error === "subscription_required"
      ) {
        // A late response must not open pricing or save another account's source.
        if (
          !user?.id ||
          submissionSessionRef.current.ownerId !== user.id ||
          submissionSessionRef.current.accessToken !== accessToken
        ) return;
        storeLandingSourceIntent(validation.url, user.id);
        navigate("/#pricing-section");
      } else if (error instanceof TopClipAutoApiError && error.kind === "rate_limited") {
        setRetryWait({
          message: error.message,
          seconds: error.retryAfterSeconds,
        });
      } else if (
        error instanceof TopClipAutoApiError &&
        error.kind === "network"
      ) {
        setSubmissionUnknown(true);
      } else if (
        error instanceof TopClipAutoApiError &&
        error.error === "invalid_settings" &&
        error.field === "clips_requested"
      ) {
        setSubmitError(IMPOSSIBLE_CLIP_COUNT_MESSAGE);
      } else {
        setSubmitAccessIssue(error instanceof TopClipAutoApiError && (
          error.error === 'subscription_inactive' || error.error === 'subscription_verification_unavailable'
        ));
        setSubmitError(readableError(error, "Your clips could not be started."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadClip = async (clip: TopClipAutoClip) => {
    if (!jobId || !accessToken) {
      setDownloadError("Your sign-in session is unavailable. Please sign in again.");
      return;
    }
    setDownloadingFilename(clip.filename);
    setDownloadError(null);
    try {
      const blob = await downloadTopClipAutoClip(
        jobId,
        clip.filename,
        accessToken
      );
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = clip.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (error) {
      setDownloadError(readableError(error, "This clip could not be downloaded."));
    } finally {
      setDownloadingFilename(null);
    }
  };

  const signInAgain = () => {
    const destination = jobId
      ? `/create-clips/${encodeURIComponent(jobId)}`
      : "/create-clips";
    storeAuthRedirectDestination(destination);
    navigate(signInPathForDestination(destination));
  };

  const createView = (
    <>
      <header className="tc-create-entry-heading">
        <h1>Create your clips</h1>
        <p>
          Add a YouTube video. TopClip finds the strongest moments and turns
          them into clips ready to post.
        </p>
      </header>

      <form
        className="tc-create-workspace"
        onSubmit={submit}
        noValidate
        data-testid="create-clips-workspace"
      >
        <section className="tc-create-workspace__section tc-create-source-zone">
          <div className="tc-create-section-heading">
            <span className="tc-create-section-heading__number">01</span>
            <div>
              <h2>Add your video</h2>
              <p>One horizontal YouTube video · 5–90 minutes.</p>
            </div>
          </div>

          <label className="tc-create-url-field" htmlFor="youtube-url">
            <Link2 aria-hidden="true" />
            <input
              ref={urlInputRef}
              id="youtube-url"
              type="url"
              value={youtubeUrl}
              aria-label="YouTube URL"
              aria-invalid={
                sourcePreviewState === "invalid" ||
                Boolean(sourceDurationError)
              }
              aria-describedby="youtube-source-feedback"
              placeholder="Paste a YouTube link"
              autoComplete="url"
              onChange={(event) => handleUrlChange(event.target.value)}
            />
            {sourcePreviewState === "loading" && (
              <Loader2
                className="tc-create-url-field__status tc-create-spin"
                aria-hidden="true"
              />
            )}
            {sourcePreviewState === "ready" && (
              <CheckCircle2
                className="tc-create-url-field__status is-ready"
                aria-hidden="true"
              />
            )}
          </label>
          <div id="youtube-source-feedback">
            <SourcePreview
              state={sourcePreviewState}
              videoId={sourceVideoId}
              message={sourcePreviewMessage}
              previewKey={sourcePreviewKey}
              onLoaded={() => setSourcePreviewState("ready")}
              onUnavailable={() => setSourcePreviewState("unavailable")}
              onReplace={replaceSource}
              onRetry={retrySourcePreview}
            />
            {sourceDurationPending && (
              <div className="tc-create-source-eligibility" role="status">
                <Loader2 className="tc-create-spin" aria-hidden="true" />
                Checking video length…
              </div>
            )}
            {sourceDurationError && (
              <div
                className="tc-create-source-eligibility is-error"
                role="alert"
              >
                <AlertCircle aria-hidden="true" />
                {sourceDurationError}
              </div>
            )}
          </div>
        </section>

        <section className="tc-create-workspace__section tc-create-settings-zone">
          <div className="tc-create-section-heading is-settings">
            <span className="tc-create-section-heading__number">02</span>
            <div>
              <h2>Shape your clips</h2>
              <p>Smart defaults are already selected.</p>
            </div>
            <span className="tc-create-default-badge">
              <Check aria-hidden="true" />
              Smart defaults
            </span>
          </div>

          <div className="tc-create-settings-grid">
            <section className="tc-create-setting">
              <header>
                <span>
                  <Scissors aria-hidden="true" />
                </span>
                <div>
                  <h3>How many clips?</h3>
                  <p>Let TopClip decide or choose an exact number.</p>
                </div>
              </header>
              <CountControl
                options={options}
                updateOptions={updateOptions}
                manualMaximum={manualClipCountMaximum}
              />
            </section>

            <section className="tc-create-setting">
              <header>
                <span>
                  <Timer aria-hidden="true" />
                </span>
                <div>
                  <h3>How long?</h3>
                  <p>Choose a target length for each clip.</p>
                </div>
              </header>
              <LengthControl options={options} updateOptions={updateOptions} />
            </section>

            <section className="tc-create-setting is-wide tc-create-caption-setting">
              <header>
                <span>
                  <Captions aria-hidden="true" />
                </span>
                <div>
                  <h3>Caption style</h3>
                  <p>Keep every clip easy to follow with sound off.</p>
                </div>
                <Toggle
                  label="Enable captions"
                  checked={options.captions_enabled}
                  onChange={(checked) =>
                    updateOptions({ captions_enabled: checked })
                  }
                />
              </header>
              <CaptionControls
                options={options}
                updateOptions={updateOptions}
                onBrowse={() => setCaptionDialogOpen(true)}
              />
            </section>

            <section className="tc-create-setting is-wide tc-create-title-setting">
              <header>
                <span>
                  <Type aria-hidden="true" />
                </span>
                <div>
                  <h3>Clip titles</h3>
                  <p>
                    TopClip automatically adds a short, engaging AI title to
                    each clip.
                  </p>
                </div>
              </header>
              <Toggle
                label="Enable AI titles"
                checked={options.ai_title_enabled}
                onChange={(checked) =>
                  updateOptions({ ai_title_enabled: checked })
                }
              />
            </section>
          </div>
        </section>

        {isSubmitting && (
          <section
            className="tc-create-submit-progress"
            aria-live="polite"
          >
            <p>
              Starting your job... This usually takes 10–20 seconds. Keep this
              page open until your job is confirmed.
            </p>
            <ProgressTracker
              activeStage={0}
              progressPercent={STAGE_PROGRESS_FLOORS[0]}
            />
          </section>
        )}

        {(retryWait || submitError || submissionUnknown) && (
          <div className="tc-create-submit-feedback">
            {retryWait && (
              <div className="tc-create-notice is-waiting" role="status">
                <Clock3 aria-hidden="true" />
                <div>
                  <strong>One generation is already active</strong>
                  <p>{retryWait.message}</p>
                  {retryWait.seconds !== null && retryWait.seconds > 0 && (
                    <span>Try again in {retryWait.seconds} seconds.</span>
                  )}
                  {retryWait.seconds === 0 && (
                    <span>You can try again now.</span>
                  )}
                </div>
              </div>
            )}

            {submitError && (
              <div className="tc-create-notice is-error" role="alert">
                <AlertCircle aria-hidden="true" />
                <div>
                  <strong>We could not start your clips</strong>
                  <p>{submitError}</p>
                  {submitAccessIssue && <div className="mt-3 flex flex-wrap gap-4">
                    <button type="button" className="min-h-11 underline" onClick={() => navigate('/dashboard')}>View Account</button>
                    <a className="inline-flex min-h-11 items-center underline" href="mailto:hello@topclip.ai?subject=TopClip%20paid%20access%20help">Contact support about paid access</a>
                  </div>}
                </div>
              </div>
            )}

            {submissionUnknown && (
              <div className="tc-create-notice is-waiting" role="status">
                <Clock3 aria-hidden="true" />
                <div>
                  <strong>Submission status unknown</strong>
                  <p>
                    Your connection ended before TopClip confirmed the request.
                    It may have reached TopClip. Wait and check again later
                    before starting another generation.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <footer className="tc-create-action-bar">
          <div>
            <span>Ready when you are</span>
            <SelectionSummary options={options} />
          </div>
          <button
            type="submit"
            className="tc-create-primary-action"
            disabled={
              !sourceCanSubmit ||
              isSubmitting ||
              submissionUnknown ||
              (retryWait?.seconds ?? 0) > 0
            }
          >
            {submissionUnknown ? (
              <>
                <Clock3 aria-hidden="true" />
                Check again later
              </>
            ) : isSubmitting ? (
              <>
                <Loader2 className="tc-create-spin" aria-hidden="true" />
                Starting your job...
              </>
            ) : (
              <>
                Create my clips
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </button>
        </footer>
      </form>
    </>
  );

  const activeStage =
    trackerStageState && trackerStageState.jobId === jobId
      ? trackerStageState.index
      : 0;
  const estimatedProgressPercent = jobTiming
    ? estimatedTopClipAutoProgressPercent(jobTiming, estimateClockMs)
    : 0;
  const trackerProgressPercent = Math.max(
    STAGE_PROGRESS_FLOORS[activeStage],
    estimatedProgressPercent
  );
  const jobAcceptanceConfirmed = Boolean(currentServerStatus || jobTiming);
  const jobIsCompleted = jobStatus?.status === "completed";
  const jobView = (
    <section
      className={`tc-create-job ${
        jobIsCompleted ? "is-complete" : jobProblem?.kind === "terminal" ? "is-failed" : ""
      }`}
      aria-live="polite"
    >
      {!jobIsCompleted && jobProblem?.kind !== "terminal" && (
        <>
          <header className="tc-create-job__header">
            <div>
              <h1>Creating your clips</h1>
              <p>
                {jobAcceptanceConfirmed
                  ? "Your clips are being created. You can safely leave this page. Finished clips will appear in My Clips."
                  : "Checking this job before showing its latest progress."}
              </p>
            </div>
            <button
              type="button"
              className="tc-create-secondary-action"
              onClick={() => navigate("/create-clips")}
            >
              <ArrowLeft aria-hidden="true" />
              Start another video
            </button>
          </header>

          <div className="tc-create-processing-card">
            {!jobStatus && !jobProblem && (
              <div className="tc-create-processing-loading" role="status">
                <span>
                  <Loader2 className="tc-create-spin" aria-hidden="true" />
                </span>
                <div>
                  <strong>Opening your clip job</strong>
                  <p>Loading the latest update.</p>
                </div>
              </div>
            )}

            {(currentServerStatus || jobProblem?.kind === "network") && (
              <ProgressTracker
                activeStage={activeStage}
                progressPercent={trackerProgressPercent}
              />
            )}

            {jobProblem?.kind === "network" && (
              <div className="tc-create-notice is-waiting">
                <Clock3 aria-hidden="true" />
                <div>
                  <strong>Connection unavailable</strong>
                  <p>{jobProblem.message}</p>
                  <button
                    type="button"
                    onClick={() => setPollVersion((value) => value + 1)}
                  >
                    <RefreshCw aria-hidden="true" />
                    Check now
                  </button>
                </div>
              </div>
            )}

            {jobProblem && jobProblem.kind !== "network" && (
                <div className="tc-create-notice is-error" role="alert">
                  <AlertCircle aria-hidden="true" />
                  <div>
                    <strong>
                      {jobProblem.kind === "authentication"
                        ? "Please sign in again"
                        : "This job is temporarily unavailable"}
                    </strong>
                    <p>{jobProblem.message}</p>
                    {jobProblem.kind === "authentication" ? (
                      <button type="button" onClick={signInAgain}>
                        Sign in again
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPollVersion((value) => value + 1)}
                      >
                        <RefreshCw aria-hidden="true" />
                        Try again
                      </button>
                    )}
                  </div>
                </div>
              )}
          </div>
          <p className="tc-create-job-reference">Job reference {jobId}</p>
        </>
      )}

      {jobProblem?.kind === "terminal" && (
        <div className="tc-create-terminal">
          <span className="tc-create-terminal__icon">
            <AlertCircle aria-hidden="true" />
          </span>
          <h1>We could not finish these clips</h1>
          <p>{jobProblem.message}</p>
          <button
            type="button"
            className="tc-create-primary-action"
            onClick={() => navigate("/create-clips")}
          >
            Try another video
            <ArrowRight aria-hidden="true" />
          </button>
          <small>Job reference {jobId}</small>
        </div>
      )}

      {jobIsCompleted && (
        <div className="tc-create-results">
          <header className="tc-create-results__header">
            <div>
              <span className="tc-create-success-label">
                <CheckCircle2 aria-hidden="true" />
                {clips.length === 0
                  ? "Generation complete"
                  : `${clips.length} ${
                      clips.length === 1 ? "clip" : "clips"
                    } ready`}
              </span>
              <h1>
                {clips.length === 0
                  ? "Your generation is complete"
                  : "Your clips are ready"}
              </h1>
              <p>
                {clips.length === 0
                  ? "TopClip is checking for finished clip files."
                  : "Preview and download your clips below."}
              </p>
            </div>
            <button
              type="button"
              className="tc-create-secondary-action"
              onClick={() => navigate("/create-clips")}
            >
              <ArrowLeft aria-hidden="true" />
              Start another video
            </button>
          </header>

          {resultsLoading && (
            <div className="tc-create-results-loading" role="status">
              <Loader2 className="tc-create-spin" aria-hidden="true" />
              <div>
                <strong>Loading your clips</strong>
                <p>Preparing private playback and download access.</p>
              </div>
            </div>
          )}

          {resultsError && (
            <div className="tc-create-notice is-waiting">
              <Clock3 aria-hidden="true" />
              <div>
                <strong>Your clips are temporarily unreachable</strong>
                <p>{resultsError}</p>
                <button type="button" onClick={() => void retryResults()}>
                  <RefreshCw aria-hidden="true" />
                  Load clips again
                </button>
              </div>
            </div>
          )}

          {!resultsLoading && !resultsError && clips.length === 0 && (
            <div className="tc-create-empty-results">
              <Film aria-hidden="true" />
              <div>
                <strong>No clip files were returned yet</strong>
                <p>
                  Try loading this completed job again. If nothing appears,
                  contact support with the job reference below.
                </p>
              </div>
              <button
                type="button"
                className="tc-create-secondary-action"
                onClick={() => void retryResults()}
              >
                <RefreshCw aria-hidden="true" />
                Load clips again
              </button>
            </div>
          )}

          {clips.length > 0 && (
            <div className="tc-create-results-grid">
              {clips.map((clip, index) => {
                const duration = formatDuration(clip.duration);
                const title =
                  clip.title || clip.hook_title || `Clip ${index + 1}`;
                return (
                  <article
                    key={clip.id || `${clip.filename}-${index}`}
                    className="tc-create-result-card"
                  >
                    <div className="tc-create-result-card__media">
                      {clip.stream_url ? (
                        <video
                          controls
                          playsInline
                          preload="metadata"
                          src={clip.stream_url}
                          aria-label={`Play ${title}`}
                        />
                      ) : (
                        <div className="tc-create-result-card__unavailable">
                          <Play aria-hidden="true" />
                          <span>Playback is temporarily unavailable.</span>
                        </div>
                      )}
                      <span>Clip {index + 1}</span>
                    </div>
                    <div className="tc-create-result-card__body">
                      <div>
                        <h2>{title}</h2>
                        {duration && <p>{duration}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => void downloadClip(clip)}
                        disabled={downloadingFilename === clip.filename}
                      >
                        {downloadingFilename === clip.filename ? (
                          <Loader2
                            className="tc-create-spin"
                            aria-hidden="true"
                          />
                        ) : (
                          <Download aria-hidden="true" />
                        )}
                        {downloadingFilename === clip.filename
                          ? "Preparing download..."
                          : "Download clip"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {downloadError && (
            <div className="tc-create-notice is-error" role="alert">
              <AlertCircle aria-hidden="true" />
              <div>
                <strong>Download unavailable</strong>
                <p>{downloadError}</p>
              </div>
            </div>
          )}

          <p className="tc-create-job-reference">Job reference {jobId}</p>
        </div>
      )}
    </section>
  );

  const journeyStep: "video" | "settings" | "clips" = jobId
    ? "clips"
    : sourceVideoId
    ? "settings"
    : "video";

  return (
    <main className="tc-create-page">
      <SubmissionGuideOffer jobId={jobId} />
      <div className="tc-create-page__glow is-one" aria-hidden="true" />
      <div className="tc-create-page__glow is-two" aria-hidden="true" />
      <div className="tc-create-page__grid" aria-hidden="true" />
      <div className="tc-create-page__inner">
        <Journey current={journeyStep} />
        {jobId ? jobView : createView}
      </div>
      {captionDialogOpen && (
        <CaptionStyleDialog
          selected={options.caption_style_id}
          onSelect={(caption_style_id) => updateOptions({ caption_style_id })}
          onClose={() => setCaptionDialogOpen(false)}
        />
      )}
    </main>
  );
};

export default TopClipAutoShell;
